import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Heading,
  Text,
  Card,
  Flex,
  Button,
  Table,
  Spinner,
} from '@radix-ui/themes';
import { ArrowLeftIcon } from '@radix-ui/react-icons';
import { useGetSignalTriggersQuery } from '../../graphql/queries/GetSignalTriggers.generated';
import TimeAgoTooltip from '../../components/TimeAgoTooltip';
import HashShortener from '../../components/HashShortener';
import MonetaryValueDisplay from '../../components/MonetaryValueDisplay';

const PAGE_SIZE = 20;

export default function SignalTriggers() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data, loading, error, fetchMore } = useGetSignalTriggersQuery({
    variables: { id: id!, first: PAGE_SIZE },
    skip: !id,
  });

  if (loading && !data) {
    return (
      <Box p="6">
        <Flex align="center" justify="center" py="9">
          <Spinner size="3" />
        </Flex>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p="6">
        <Card>
          <Text color="red">Error loading triggers: {error.message}</Text>
        </Card>
      </Box>
    );
  }

  const signal = data?.signal;
  if (!signal) {
    return (
      <Box p="6">
        <Card>
          <Text color="gray">Signal not found</Text>
        </Card>
      </Box>
    );
  }

  const triggers = signal.triggers.edges.map((edge) => edge.node);
  const pageInfo = signal.triggers.pageInfo;
  const totalCount = signal.triggers.totalCount;

  const handleLoadMore = () => {
    if (pageInfo.hasNextPage && pageInfo.endCursor) {
      fetchMore({
        variables: {
          after: pageInfo.endCursor,
        },
      });
    }
  };

  return (
    <Box p="6">
      <Flex direction="column" gap="4">
        <Flex align="center" gap="3">
          <Button
            variant="ghost"
            size="2"
            onClick={() => navigate(`/signals/${id}`)}
          >
            <ArrowLeftIcon />
            Back to Signal
          </Button>
        </Flex>

        <Flex justify="between" align="center">
          <Box>
            <Heading size="6">{signal.name} - Triggers</Heading>
            <Text size="2" color="gray">
              {totalCount} total trigger{totalCount !== 1 ? 's' : ''}
            </Text>
          </Box>
        </Flex>

        <Card>
          {triggers.length === 0 ? (
            <Flex align="center" justify="center" py="6">
              <Text color="gray">No triggers recorded yet</Text>
            </Flex>
          ) : (
            <Table.Root>
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeaderCell>Triggered At</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Transaction</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Block</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>From</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>To</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Value</Table.ColumnHeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {triggers.map((trigger) => (
                  <Table.Row key={trigger.id}>
                    <Table.Cell>
                      <TimeAgoTooltip timestamp={trigger.triggeredAt} />
                    </Table.Cell>
                    <Table.Cell>
                      {trigger.transaction?.hash ? (
                        <HashShortener hash={trigger.transaction.hash} />
                      ) : (
                        <Text color="gray">-</Text>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      {trigger.transaction?.blockNumber ?? (
                        <Text color="gray">-</Text>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      {trigger.transaction?.from?.address ? (
                        <HashShortener hash={trigger.transaction.from.address} />
                      ) : (
                        <Text color="gray">-</Text>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      {trigger.transaction?.to?.address ? (
                        <HashShortener hash={trigger.transaction.to.address} />
                      ) : (
                        <Text color="gray">-</Text>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      {trigger.transaction?.value ? (
                        <MonetaryValueDisplay
                          value={trigger.transaction.value}
                        />
                      ) : (
                        <Text color="gray">-</Text>
                      )}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
          )}

          {pageInfo.hasNextPage && (
            <Flex justify="center" py="4">
              <Button variant="soft" onClick={handleLoadMore} disabled={loading}>
                {loading ? 'Loading...' : 'Load More'}
              </Button>
            </Flex>
          )}
        </Card>
      </Flex>
    </Box>
  );
}
